import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  TableSortLabel,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Menu,
  ListItemText,
  Snackbar,
  Alert
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { usePermissions } from '../hooks/usePermissions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ColumnFilter, { FilterConfig } from './ColumnFilter';
import { getCategoryOptions } from '../constants/categories';

interface ShopInventoryRequest {
  id: number;
  createdDate: string;
  dispatchedAt: string;
  shop: {
    id: number;
    name: string;
    address: string;
  };
  items: {
    id: number;
    item: {
      id: number;
      name: string;
      quantityUnit: string;
      pricePerUnit: number | null;
      addAmountToBillInPOS: boolean;
      manufacturedInFactory: boolean;
    };
    quantity: number;
  }[];
  status: 'REQUESTED' | 'DISPATCHED';
}

interface ShopInventoryRequestItem {
  itemName: string;
  quantity: number;
}

interface Item {
  id: number;
  name: string;
  quantityUnit: string;
  pricePerUnit: number | null;
  addAmountToBillInPOS: boolean;
  manufacturedInFactory: boolean;
  category: string;
  consumable: number;
}

interface Shop {
  id: number;
  name: string;
  address: string;
}

type Order = 'asc' | 'desc';

// Utility function to convert UTC date string to browser's local timezone
const convertUTCToLocalTime = (utcDateString: string): string => {
  try {
    // The server is in UTC timezone, so database dates are in UTC
    // Format: "yyyy-MM-dd HH:mm:ss" (UTC time)
    let dateString = utcDateString;
    
    // Convert space to 'T' and add 'Z' to make it explicit UTC
    if (dateString.includes(' ')) {
      dateString = dateString.replace(' ', 'T') + 'Z';
    } else if (dateString.includes('T') && !dateString.includes('Z')) {
      dateString = dateString + 'Z';
    }
    
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', utcDateString);
      return utcDateString;
    }
    
    // Return formatted date in local timezone
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error converting date:', utcDateString, error);
    return utcDateString;
  }
};

const Requests: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || '';
  const { hasPermission } = usePermissions(role);
  
  const [requests, setRequests] = useState<ShopInventoryRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [showRequestDetailsDialog, setShowRequestDetailsDialog] = useState(false);
  const [showAddRequestDialog, setShowAddRequestDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ShopInventoryRequest | null>(null);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('createdDate');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatchInFlightRef = useRef(false);
  const [editableQuantities, setEditableQuantities] = useState<{ [key: number]: number }>({});
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  
  // Add Request states
  const [items, setItems] = useState<Item[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [newRequest, setNewRequest] = useState({
    shopName: '',
    items: [] as ShopInventoryRequestItem[],
    category: ''
  });
  const [newRequestItem, setNewRequestItem] = useState({
    itemName: '',
    quantity: 0
  });
  
  // Toast notification states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (token) {
      fetchRequests();
    }
  }, [token]);

  // Helper functions for toast notifications
  const showSuccess = (message: string) => {
    setToastMessage(message);
    setToastSeverity('success');
    setShowToast(true);
  };

  const showError = (message: string) => {
    setToastMessage(message);
    setToastSeverity('error');
    setShowToast(true);
  };

  const fetchRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const shopName = localStorage.getItem('shopName');
      const userRole = localStorage.getItem('role') || '';
      
      let url = shopName 
        ? `${API_BASE_URL}/shop-inventory-requests/shop/${shopName}`
        : `${API_BASE_URL}/shop-inventory-requests`;
      
      // For non-Administrator/Director roles, add startDate parameter for last 5 days
      if (shopName && userRole !== 'Administrator' && userRole !== 'Director') {
        const fiveDaysAgo = new Date();
        fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);
        const startDate = fiveDaysAgo.toISOString().slice(0, 19).replace('T', ' ');
        url += `?startDate=${encodeURIComponent(startDate)}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: token }
      });
      setRequests(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching requests:', err);
      }
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const columnFilterConfigs: FilterConfig[] = [
    { field: 'shop', type: 'select', label: 'Shop', options: shops.map(shop => ({ value: shop.name, label: shop.name })) },
    { field: 'createdDate', type: 'date', label: 'Created Date' },
    { field: 'dispatchedAt', type: 'date', label: 'Dispatched Date' },
    { field: 'status', type: 'select', label: 'Status', options: Array.from(new Set(requests.map(request => request.status))).map(status => ({
      value: status,
      label: status
    })) }
  ];

  const handleColumnFilterChange = (field: string, value: any) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleColumnFilterClear = (field: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const getFilteredAndSortedRequests = () => {
    let filtered = requests;

    // Apply column filters
    filtered = filtered.filter(request => {
      return Object.entries(columnFilters).every(([field, filterValue]) => {
        if (!filterValue || filterValue === '') return true;
        
        switch (field) {
          case 'shop':
            return request.shop.name === filterValue;
          case 'createdDate':
            if (filterValue.start && filterValue.end) {
              const filterStartDate = new Date(filterValue.start + 'T00:00:00');
              const filterEndDate = new Date(filterValue.end + 'T23:59:59');
              const requestDate = new Date(request.createdDate);
              return requestDate >= filterStartDate && requestDate <= filterEndDate;
            }
            return true;
          case 'dispatchedAt':
            if (filterValue.start && filterValue.end) {
              const filterStartDate = new Date(filterValue.start + 'T00:00:00');
              const filterEndDate = new Date(filterValue.end + 'T23:59:59');
              const requestDate = new Date(request.dispatchedAt);
              return requestDate >= filterStartDate && requestDate <= filterEndDate;
            }
            return true;
          case 'status':
            return request.status === filterValue;
          default:
            return true;
        }
      });
    });

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (orderBy) {
        case 'createdDate':
          aValue = new Date(a.createdDate).getTime();
          bValue = new Date(b.createdDate).getTime();
          break;
        case 'dispatchedAt':
          aValue = new Date(a.dispatchedAt).getTime();
          bValue = new Date(b.dispatchedAt).getTime();
          break;
        case 'shop':
          aValue = a.shop.name.toLowerCase();
          bValue = b.shop.name.toLowerCase();
          break;
        case 'status':
          aValue = a.status.toLowerCase();
          bValue = b.status.toLowerCase();
          break;
        default:
          aValue = a[orderBy as keyof ShopInventoryRequest];
          bValue = b[orderBy as keyof ShopInventoryRequest];
      }
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  const handleDeleteRequest = async (requestId: number) => {
    if (!window.confirm('Are you sure you want to delete this request?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/shop-inventory-requests/${requestId}`, {
        headers: { Authorization: token }
      });
      fetchRequests();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error deleting request:', err);
      }
    }
  };

  const handleDispatchRequest = async (request: ShopInventoryRequest) => {
    if (dispatchInFlightRef.current) {
      return;
    }
    dispatchInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      // Create a new array of items with modified quantities if they exist
      const itemsToDispatch = request.items.map(item => ({
        ...item,
        quantity: editableQuantities[item.id] ?? item.quantity
      }));

      await axios.post(`${API_BASE_URL}/inventory-transactions/dispatch/${request.id}`, {
        items: itemsToDispatch
      }, {
        headers: { Authorization: token }
      });

      showSuccess('Request dispatched successfully');
      fetchRequests();
      setShowRequestDetailsDialog(false);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        const errorMessage = err.response?.data?.message || err.response?.data || 'Failed to dispatch request';
        showError(typeof errorMessage === 'string' ? errorMessage : 'Failed to dispatch request');
        console.error('Error dispatching request:', err);
      }
    } finally {
      dispatchInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleQuantityChange = (itemId: number, quantity: number) => {
    setEditableQuantities(prev => ({
      ...prev,
      [itemId]: quantity
    }));
  };

  const handleAddRequestClick = () => {
    setShowAddRequestDialog(true);
    fetchShopItemsForRequest();
  };

  const fetchShopItemsForRequest = async () => {
    setIsLoadingItems(true);
    try {
      const shopName = localStorage.getItem('shopName');
      if (!shopName) {
        console.error('Shop name not found. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/inventory-transactions/status/shop/${encodeURIComponent(shopName)}`, {
        headers: { Authorization: token }
      });
      
      // Transform the data to match the Item interface
      const transformedItems = response.data.map((item: any, index: number) => ({
        id: index + 1, // Generate a temporary ID since the DTO doesn't have one
        name: item.itemName, // Map itemName to name
        quantityUnit: item.quantityUnit,
        pricePerUnit: item.pricePerUnit || 0,
        addAmountToBillInPOS: item.addAmountToBillInPOS ?? true,
        manufacturedInFactory: item.manufacturedInFactory ?? false,
        category: item.category,
        consumable: item.consumable || 0
      }));
      
      setItems(transformedItems);
      
      // Prepopulate shop name for Shop POS Operator
      setNewRequest({
        shopName: shopName,
        items: [],
        category: ''
      });
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching shop items:', err);
      }
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleAddRequestItem = async () => {
    if (!newRequestItem.itemName || newRequestItem.quantity <= 0) {
      showError('Please select an item and enter a valid quantity');
      return;
    }

    // Check if item already exists in the request
    if (newRequest.items.some(item => item.itemName === newRequestItem.itemName)) {
      showError('This item has already been added to the request');
      return;
    }

    try {
      // Fetch inventory status to check available quantity
      const response = await axios.get(`${API_BASE_URL}/inventory-transactions/status`, {
        headers: { Authorization: token }
      });
      
      const inventoryStatus = response.data;
      const selectedItem = inventoryStatus.find((item: any) => item.itemName === newRequestItem.itemName);
      
      if (!selectedItem) {
        showError('Item not found in inventory');
        return;
      }

      // Check if the item is manufactured in factory
      const itemDetails = items.find(item => item.name === newRequestItem.itemName);
      if (!itemDetails?.manufacturedInFactory && selectedItem.consumable < newRequestItem.quantity) {
        showError(`Not enough quantity available. Available: ${selectedItem.consumable} ${selectedItem.quantityUnit}`);
        return;
      }

      setNewRequest({
        ...newRequest,
        items: [...newRequest.items, newRequestItem]
      });
      setNewRequestItem({ itemName: '', quantity: 0 });
      showSuccess('Item added to request successfully');
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        showError('You do not have permission to add items');
      } else {
        // Show the specific error message from the backend
        const errorMessage = err.response?.data?.message || err.response?.data || 'Failed to check inventory status';
        showError(errorMessage);
      }
    }
  };

  const handleCloseAddRequestDialog = () => {
    setShowAddRequestDialog(false);
    setNewRequest({ shopName: '', items: [], category: '' });
    setNewRequestItem({ itemName: '', quantity: 0 });
  };

  const handleRemoveRequestItem = (index: number) => {
    const updatedItems = [...newRequest.items];
    updatedItems.splice(index, 1);
    setNewRequest({ ...newRequest, items: updatedItems });
  };

  const handleAddRequest = async () => {
    setIsSubmitting(true);
    try {
      const shopName = localStorage.getItem('shopName');
      const requestData = {
        ...newRequest,
        shopName: shopName || newRequest.shopName
      };

      await axios.post(`${API_BASE_URL}/shop-inventory-requests`, requestData, {
        headers: { Authorization: token }
      });
      
      handleCloseAddRequestDialog();
      fetchRequests();
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 403) {
        console.error('You do not have permission to add requests');
      } else {
        console.error('Failed to add request');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (action: string) => {
    handleActionsMenuClose();
    switch (action) {
      case 'add':
        handleAddRequestClick();
        break;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {isMobile ? (
        <Box sx={{ mb: 2 }}>
          {hasPermission('add-request') && (
            <>
              <Button
                variant="outlined"
                onClick={handleActionsMenuOpen}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  py: 1.5,
                  fontSize: '1rem'
                }}
              >
                Actions
              </Button>
              <Menu
                anchorEl={actionsMenuAnchor}
                open={Boolean(actionsMenuAnchor)}
                onClose={handleActionsMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: '70vh',
                    width: '100%',
                    maxWidth: '280px'
                  }
                }}
              >
                <MenuItem onClick={() => handleActionSelect('add')}>
                  <ListItemText primary="Add Request" />
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, maxWidth: '600px' }}>
              {/* Search can be added here if needed */}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
              {hasPermission('add-request') && (
                <Button variant="contained" onClick={handleAddRequestClick} size="small">
                  Add Request
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      )}
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            fontSize: '0.875rem'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Shop
                  <ColumnFilter
                    config={columnFilterConfigs[0]}
                    value={columnFilters.shop}
                    onChange={(value) => handleColumnFilterChange('shop', value)}
                    onClear={() => handleColumnFilterClear('shop')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TableSortLabel
                    active={orderBy === 'createdDate'}
                    direction={orderBy === 'createdDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdDate')}
                  >
                    Created Date
                  </TableSortLabel>
                  <ColumnFilter
                    config={columnFilterConfigs[1]}
                    value={columnFilters.createdDate}
                    onChange={(value) => handleColumnFilterChange('createdDate', value)}
                    onClear={() => handleColumnFilterClear('createdDate')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '120px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Dispatched Date
                  <ColumnFilter
                    config={columnFilterConfigs[2]}
                    value={columnFilters.dispatchedAt}
                    onChange={(value) => handleColumnFilterChange('dispatchedAt', value)}
                    onClear={() => handleColumnFilterClear('dispatchedAt')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '100px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  <ColumnFilter
                    config={columnFilterConfigs[3]}
                    value={columnFilters.status}
                    onChange={(value) => handleColumnFilterChange('status', value)}
                    onClear={() => handleColumnFilterClear('status')}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: '150px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingRequests ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : getFilteredAndSortedRequests().length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No requests found
                </TableCell>
              </TableRow>
            ) : (
              getFilteredAndSortedRequests().map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.shop.name}</TableCell>
                    <TableCell>{convertUTCToLocalTime(request.createdDate)}</TableCell>
                    <TableCell>{request.dispatchedAt ? convertUTCToLocalTime(request.dispatchedAt) : '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={request.status} 
                        color={request.status === 'REQUESTED' ? 'warning' : 'success'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {hasPermission('view-request-details') && (
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowRequestDetailsDialog(true);
                          }}
                          disabled={isSubmitting}
                        >
                          View Details
                        </Button>
                      )}
                      {request.status === 'REQUESTED' && hasPermission('delete-request') && (
                        <Button 
                          variant="contained" 
                          color="error" 
                          size="small" 
                          onClick={() => handleDeleteRequest(request.id)}
                          disabled={isSubmitting}
                          sx={{ ml: 1 }}
                        >
                          Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Request Details Dialog */}
      <Dialog open={showRequestDetailsDialog} onClose={() => setShowRequestDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Typography variant="subtitle1">Shop: {selectedRequest.shop.name}</Typography>
              <Typography variant="subtitle1">Created Date: {convertUTCToLocalTime(selectedRequest.createdDate)}</Typography>
              <Typography variant="subtitle1">Dispatched Date: {selectedRequest.dispatchedAt ? convertUTCToLocalTime(selectedRequest.dispatchedAt) : 'Not dispatched yet'}</Typography>
              <Typography variant="subtitle1">Status: 
                <Chip 
                  label={selectedRequest.status} 
                  color={selectedRequest.status === 'REQUESTED' ? 'warning' : 'success'}
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedRequest.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.item.name}</TableCell>
                        <TableCell>
                          {hasPermission('dispatch-request') && selectedRequest.status === 'REQUESTED' && item.item.manufacturedInFactory ? (
                            <TextField
                              type="number"
                              value={editableQuantities[item.id] ?? item.quantity}
                              onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 0)}
                              InputProps={{
                                inputProps: { min: 0, max: item.quantity }
                              }}
                              size="small"
                              sx={{ width: 100 }}
                            />
                          ) : (
                            item.quantity
                          )}
                        </TableCell>
                        <TableCell>{item.item.quantityUnit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            onClick={() => {
              if (selectedRequest) {
                const doc = new jsPDF();
                const generatedDate = new Date().toLocaleString();
                
                // Add title
                doc.setFontSize(16);
                doc.text('Shop Inventory Request', 14, 15);
                
                // Add request information
                doc.setFontSize(12);
                doc.text(`Shop: ${selectedRequest.shop.name}`, 14, 25);
                doc.text(`Created Date: ${convertUTCToLocalTime(selectedRequest.createdDate)}`, 14, 35);
                doc.text(`Dispatched Date: ${selectedRequest.dispatchedAt ? convertUTCToLocalTime(selectedRequest.dispatchedAt) : 'Not dispatched yet'}`, 14, 45);
                doc.text(`Status: ${selectedRequest.status}`, 14, 55);
                doc.text(`PDF Generated on: ${generatedDate}`, 14, 65);
                
                // Add items table
                const tableData = selectedRequest.items.map(item => [
                  item.item.name,
                  item.quantity.toString(),
                  item.item.quantityUnit
                ]);
                
                autoTable(doc, {
                  head: [['Item Name', 'Quantity', 'Unit']],
                  body: tableData,
                  startY: 75,
                  theme: 'grid',
                  styles: { fontSize: 10 },
                  headStyles: { fillColor: [41, 128, 185] }
                });
                
                doc.save(`request-${selectedRequest.id}.pdf`);
              }
            }}
          >
            Export to PDF
          </Button>
          {selectedRequest?.status === 'REQUESTED' && hasPermission('dispatch-request') && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => handleDispatchRequest(selectedRequest)}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Dispatch'}
            </Button>
          )}
          <Button onClick={() => setShowRequestDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Request Dialog */}
      <Dialog open={showAddRequestDialog} onClose={handleCloseAddRequestDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add New Request</DialogTitle>
        <DialogContent>
          {!localStorage.getItem('shopName') && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Shop</InputLabel>
              <Select
                value={newRequest.shopName}
                onChange={(e) => setNewRequest({ ...newRequest, shopName: e.target.value })}
                label="Shop"
                disabled={isSubmitting}
              >
                {shops.map((shop) => (
                  <MenuItem key={shop.id} value={shop.name}>
                    {shop.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Category</InputLabel>
            <Select
              value={newRequest.category}
              onChange={(e) => {
                setNewRequest({ ...newRequest, category: e.target.value });
                setNewRequestItem({ ...newRequestItem, itemName: '' });
              }}
              label="Category"
              disabled={isSubmitting}
            >
              {getCategoryOptions().map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {newRequest.category && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
              <FormControl fullWidth>
                <InputLabel>Item</InputLabel>
                <Select
                  value={newRequestItem.itemName}
                  onChange={(e) => setNewRequestItem({ ...newRequestItem, itemName: e.target.value })}
                  label="Item"
                  disabled={isSubmitting}
                >
                  {items
                    .filter(item => item.category === newRequest.category)
                    .sort((a, b) => {
                      // First sort by remaining quantity (least first)
                      if (a.consumable !== b.consumable) {
                        return a.consumable - b.consumable;
                      }
                      // Then sort alphabetically by name
                      return a.name.localeCompare(b.name);
                    })
                    .map((item) => {
                      const isZeroRemaining = item.consumable <= 0;
                      return (
                        <MenuItem 
                          key={item.id} 
                          value={item.name}
                          sx={{
                            color: isZeroRemaining ? 'red' : 'inherit'
                          }}
                        >
                          {item.name} - Remaining at Shop: {item.consumable}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              <TextField
                label="Quantity"
                type="number"
                value={newRequestItem.quantity}
                onChange={(e) => setNewRequestItem({ ...newRequestItem, quantity: parseInt(e.target.value) })}
                InputProps={{
                  inputProps: { min: 1 }
                }}
                disabled={isSubmitting}
              />
              <Button variant="contained" onClick={handleAddRequestItem} disabled={isSubmitting}>
                Add Item
              </Button>
            </Box>
          )}
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {newRequest.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.itemName}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small"
                        onClick={() => handleRemoveRequestItem(index)}
                        disabled={isSubmitting}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddRequestDialog} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleAddRequest} disabled={isSubmitting || newRequest.items.length === 0}>
            {isSubmitting ? <CircularProgress size={24} /> : 'Add Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={showToast}
        autoHideDuration={6000}
        onClose={() => setShowToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowToast(false)} severity={toastSeverity}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Requests;
